import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, Switch,
    message, Popconfirm, Tag, Space, Tooltip
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    QuestionCircleOutlined, MedicineBoxOutlined, SaveOutlined
} from '@ant-design/icons';
import AYSService from '../../services/AYSService';
import FacilityService from '../../services/FacilityService';
import util from '../../utils/util';

const { TextArea } = Input;

export default function QoCAYSAdmin() {
    const [activeTab, setActiveTab]   = useState('questions');
    // Permission checks
    const canAdd    = util.canAdd('ays_admin');
    const canEdit   = util.canEdit('ays_admin');
    const canDelete = util.canDelete('ays_admin');
    const canExport = util.canExport('ays_admin');
    const [questions, setQuestions]   = useState([]);
    const [qLoading, setQLoading]     = useState(false);
    const [qModal, setQModal]         = useState(false);
    const [editQ, setEditQ]           = useState(null);
    const [qForm]                     = Form.useForm();
    const [facilities, setFacilities] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [fLoading, setFLoading]     = useState(false);
    const [fModal, setFModal]         = useState(false);
    const [fForm]                     = Form.useForm();

    useEffect(() => { loadQuestions(); loadFacilities(); }, []); // eslint-disable-line

    const loadQuestions = () => {
        setQLoading(true);
        AYSService.getQuestions()
            .then(r => setQuestions(r.data?.data || []))
            .catch(e => message.error(e.message))
            .finally(() => setQLoading(false));
    };

    const loadFacilities = () => {
        setFLoading(true);
        AYSService.getFacilities()
            .then(r => setFacilities(r.data?.data || []))
            .catch(e => message.error(e.message))
            .finally(() => setFLoading(false));
        FacilityService.all()
            .then(r => setAllClients(r.data?.result?.data || r.data?.data || []))
            .catch(() => {});
    };

    const openQModal = (record = null) => {
        setEditQ(record);
        qForm.setFieldsValue(record
            ? { ...record, status: record.status === '1' || record.status === 1, is_key_pop: record.is_key_pop === '1' || record.is_key_pop === 1 }
            : { section: 'General', status: true, is_key_pop: false }
        );
        setQModal(true);
    };

    const saveQuestion = () => {
        qForm.validateFields().then(values => {
            const data = { ...values, id: editQ?.id || 0, status: values.status ? 1 : 0, is_key_pop: values.is_key_pop ? 1 : 0 };
            util.showLoader();
            AYSService.saveQuestion(data)
                .then(r => { if (r.data?.code !== 200) throw new Error(r.data?.message); message.success(r.data.message); setQModal(false); loadQuestions(); })
                .catch(e => message.error(e.message))
                .finally(() => util.hideLoader());
        });
    };

    const deleteQuestion = (id) => {
        util.showLoader();
        AYSService.deleteQuestion(id)
            .then(() => { message.success('Question deleted'); loadQuestions(); })
            .catch(e => message.error(e.message))
            .finally(() => util.hideLoader());
    };

    const openFModal = () => {
        fForm.resetFields();
        fForm.setFieldsValue({ enrolled_at: new Date().toISOString().split('T')[0], status: true });
        setFModal(true);
    };

    const saveFacility = () => {
        fForm.validateFields().then(values => {
            util.showLoader();
            AYSService.saveFacility({ ...values, status: values.status ? 1 : 0 })
                .then(r => { if (r.data?.code !== 200) throw new Error(r.data?.message); message.success(r.data.message); setFModal(false); loadFacilities(); })
                .catch(e => message.error(e.message))
                .finally(() => util.hideLoader());
        });
    };

    const removeFacility = (id) => {
        util.showLoader();
        AYSService.removeFacility(id)
            .then(() => { message.success('Facility removed'); loadFacilities(); })
            .catch(e => message.error(e.message))
            .finally(() => util.hideLoader());
    };

    const qCols = [
        { title: '#', dataIndex: 'display_order', width: 50, sorter: (a,b) => a.display_order - b.display_order },
        { title: 'Question', dataIndex: 'question_text', render: (t, r) => (
            <Space direction="vertical" size={2}>
                <span>{t}</span>
                <Space size={4}>
                    <Tag color={r.section === 'Key Population' ? 'purple' : 'blue'} style={{fontSize:10}}>{r.section}</Tag>
                    {(r.is_key_pop === '1' || r.is_key_pop === 1) && <Tag color="magenta" style={{fontSize:10}}>KP</Tag>}
                </Space>
            </Space>
        )},
        { title: 'Status', dataIndex: 'status', width: 80, render: v =>
            <Tag color={v === '1' || v === 1 ? 'green' : 'red'}>{v === '1' || v === 1 ? 'Active' : 'Inactive'}</Tag>
        },
        { title: '', width: 80, render: (_, r) => (
            <Space>
                <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openQModal(r)} /></Tooltip>
                <Popconfirm title="Delete this question?" onConfirm={() => deleteQuestion(r.id)}>
                    <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
                </Popconfirm>
            </Space>
        )},
    ];

    const fCols = [
        { title: 'Facility', dataIndex: 'business_name' },
        { title: 'Type', dataIndex: 'site_type', width: 160 },
        { title: 'Region', dataIndex: 'located_region', width: 140 },
        { title: 'Enrolled', dataIndex: 'enrolled_at', width: 120 },
        { title: 'Status', dataIndex: 'status', width: 80, render: v =>
            <Tag color={v === '1' || v === 1 ? 'green' : 'red'}>{v === '1' || v === 1 ? 'Active' : 'Inactive'}</Tag>
        },
        { title: '', width: 80, render: (_, r) => (
            <Popconfirm title="Remove this facility?" onConfirm={() => removeFacility(r.id)}>
                <Button size="small" danger icon={<DeleteOutlined />}>Remove</Button>
            </Popconfirm>
        )},
    ];

    const tabStyle = (key) => ({
        padding: '8px 20px', cursor: 'pointer', userSelect: 'none',
        fontWeight: activeTab === key ? 700 : 400,
        color: activeTab === key ? '#1890ff' : '#595959',
        borderBottom: activeTab === key ? '2px solid #1890ff' : '2px solid transparent',
    });

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>AYS Scorecard - Administration</h4>
                    <small className="text-muted">Adolescent & Youth Services - Question & Facility Management</small>
                </div>
            </div>

            <div className="page-pad">
                {/* Tab bar */}
                <div style={{ borderBottom: '1px solid #f0f0f0', marginBottom: 16, display: 'flex' }}>
                    <div style={tabStyle('questions')} onClick={() => setActiveTab('questions')}>
                        <QuestionCircleOutlined /> Questions ({questions.length})
                    </div>
                    <div style={tabStyle('facilities')} onClick={() => setActiveTab('facilities')}>
                        <MedicineBoxOutlined /> Enrolled Facilities ({facilities.length})
                    </div>
                </div>

                {/* Questions panel */}
                {activeTab === 'questions' && (
                    <div>
                        <div className="d-flex mb-3">
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openQModal()}>Add Question</Button>
                            <small className="text-muted my-auto ml-3">
                                {questions.filter(q => q.status === '1' || q.status === 1).length} active -
                                Always=4, Mostly=3, Sometimes=2, Never=1 - Mean 3.5+ = Satisfied
                            </small>
                        </div>
                        <Table rowKey="id" dataSource={questions} columns={qCols} loading={qLoading} size="small" pagination={false} />
                    </div>
                )}

                {/* Facilities panel */}
                {activeTab === 'facilities' && (
                    <div>
                        <div className="d-flex mb-3">
                            <Button type="primary" icon={<PlusOutlined />} onClick={openFModal}>Enrol Facility</Button>
                            <small className="text-muted my-auto ml-3">Only enrolled facilities can submit AYS scorecards</small>
                        </div>
                        <Table rowKey="id" dataSource={facilities} columns={fCols} loading={fLoading} size="small" pagination={{ pageSize: 20 }} />
                    </div>
                )}
            </div>

            {/* Question Modal */}
            <Modal title={editQ ? 'Edit Question' : 'Add Question'} open={qModal}
                onCancel={() => setQModal(false)} onOk={saveQuestion}
                okText={<span><SaveOutlined /> Save</span>} width={600}>
                <Form form={qForm} layout="vertical">
                    <Form.Item label="Question Text" name="question_text" rules={[{ required: true, message: 'Required' }]}>
                        <TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Section" name="section" rules={[{ required: true }]}>
                        <Select options={[{ value: 'General', label: 'General' }, { value: 'Key Population', label: 'Key Population' }]} />
                    </Form.Item>
                    <Form.Item label="Display Order" name="display_order">
                        <Input type="number" min={0} style={{ width: 100 }} />
                    </Form.Item>
                    <Form.Item label="Key Population?" name="is_key_pop" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item label="Active" name="status" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            {/* Facility Modal */}
            <Modal title="Enrol Facility in AYS Programme" open={fModal}
                onCancel={() => setFModal(false)} onOk={saveFacility}
                okText={<span><SaveOutlined /> Save</span>}>
                <Form form={fForm} layout="vertical">
                    <Form.Item label="Facility" name="facility_id" rules={[{ required: true, message: 'Select a facility' }]}>
                        <Select showSearch placeholder="Search facility..."
                            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                            options={allClients.map(c => ({ value: c.id, label: c.business_name }))} />
                    </Form.Item>
                    <Form.Item label="Enrolment Date" name="enrolled_at" rules={[{ required: true }]}>
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <TextArea rows={2} placeholder="Optional notes" />
                    </Form.Item>
                    <Form.Item label="Active" name="status" valuePropName="checked"><Switch defaultChecked /></Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
